/**
 * RHCSA Landing Page - SEO Optimized
 * Route: /rhcsa
 */
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, ChevronRight, Terminal, Server, Shield, Lightbulb, Star, CheckCircle, Users, BookOpen, Monitor, Clock, Award, Lock } from 'lucide-react';
import { useCurrency } from '../hooks/useCurrency';

const GRRAS_LOGO = '/images/grras-logo.png';

const RHCSALanding = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const { isIndia } = useCurrency();
  const fp = (inr, usd) => isIndia ? `\u20B9${inr.toLocaleString('en-IN')}` : `$${usd}`;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const topics = [
    'Essential Linux Commands', 'File System Management', 'User & Group Management',
    'Process & Service Management', 'Shell Scripting & Automation', 'SELinux Configuration',
    'Storage & LVM', 'Network Configuration', 'SSH & Security',
    'Package Management (DNF/RPM)', 'Containers (Podman)', 'Scheduled Tasks (Cron)'
  ];

  const features = [
    { title: 'Real RHEL 10 VM', desc: 'Practice on actual Red Hat Enterprise Linux 10, not simulators', icon: Server },
    { title: 'Auto-Verified Tasks', desc: 'Instant pass/fail feedback on every command you run', icon: CheckCircle },
    { title: 'Command Hints', desc: "Stuck on a task? Get hints without leaving the lab", icon: Lightbulb },
    { title: 'Dual VM Setup', desc: 'Exactly like the real RHCSA exam environment', icon: Monitor },
    { title: 'Mock Tests', desc: '30+ RHCSA mock tests with 895+ questions for interview prep', icon: BookOpen },
    { title: 'Authorized Partner', desc: 'GRRAS is Red Hat Authorized Partner since 2008', icon: Award },
  ];

  const testimonials = [
    { name: 'Aaryan', role: 'RHCSA Certified', quote: 'Cleared RHCSA in first attempt. The real VM practice made all the difference. Felt exactly like the actual exam.', certImage: '/certificates/aaryan_rhcsa.png' },
    { name: 'Keerti', role: 'RHCE Certified', quote: 'The 3 exam sets are brilliant. I practiced all 3 sets multiple times and cleared the exam with confidence.', certImage: '/certificates/keerti_rhce.png' },
    { name: 'Sagar', role: 'Red Hat Containers Specialist', quote: 'Auto-verified tasks helped me know exactly where I was going wrong. Best RHCSA prep platform in India.', certImage: '/certificates/sagar_containers.png' },
    { name: 'Ankit', role: 'OpenShift Administrator (EX280)', quote: 'Command hints saved me so many times. GRRAS Lab is the only platform that gives real RHEL VM practice.', certImage: '/certificates/ankit_openshift.png' },
  ];

  const faqs = [
    { q: 'What is RHCSA and who should practice here?', a: 'RHCSA (Red Hat Certified System Administrator) is a globally recognized Linux certification. This platform is for anyone preparing for the RHCSA EX200 exam.' },
    { q: 'Is this a real RHEL VM or a simulator?', a: 'It is a real RHEL 10 Virtual Machine — not a simulator. You get an isolated VM just like the actual exam environment.' },
    { q: 'How many exam sets are available?', a: '3 complete RHCSA exam sets with 45 questions each, dual VM setup, timed environment — exactly like the real exam.' },
    { q: 'Do I need prior Linux experience?', a: 'Basic Linux knowledge is helpful. We also have RH124 and RH134 courses for beginners starting from scratch.' },
    { q: 'Is there a free trial?', a: 'Yes! Register free and access select labs without any payment. No credit card required.' },
    { q: 'Is GRRAS an authorized Red Hat partner?', a: 'Yes! GRRAS Solutions has been an authorized Red Hat Training and Business Partner since 2008.' },
    { q: 'How long do I have access after purchase?', a: 'Lifetime access. Purchase once and practice anytime, anywhere, forever.' },
  ];

  return (
    <>
      <Helmet>
        <title>RHCSA Exam Practice Online | Real RHEL 10 VM Labs | GRRAS</title>
        <meta name="description" content="Practice RHCSA on real RHEL 10 Virtual Machine. 3 full exam sets, 45 questions each, auto-verified tasks & hints. Free signup. Powered by GRRAS Solutions." />
        <meta name="keywords" content="rhcsa exam practice, rhcsa online lab, rhcsa mock test, rhcsa preparation, red hat certification practice, linux certification lab, rhcsa training india, rhcsa free practice" />
        <link rel="canonical" href="https://lab.grras.tech/rhcsa" />
        <meta property="og:title" content="RHCSA Exam Practice — Real RHEL 10 VM | GRRAS Lab" />
        <meta property="og:description" content="3 full RHCSA exam sets, real RHEL 10 VM, auto-verified tasks & hints. Free signup at GRRAS Lab. India's best RHCSA practice platform." />
        <meta property="og:image" content={GRRAS_LOGO} />
        <meta property="og:url" content="https://lab.grras.tech/rhcsa" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Course",
          "name": "RHCSA Exam Practice Labs",
          "description": "Practice RHCSA on real RHEL 10 VM. 3 full exam sets with auto-verified tasks.",
          "provider": { "@type": "Organization", "name": "GRRAS Solutions Pvt. Ltd.", "url": "https://lab.grras.tech" },
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": "INR", "availability": "https://schema.org/InStock" }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "GRRAS Solutions Pvt. Ltd.",
          "url": "https://lab.grras.tech",
          "logo": GRRAS_LOGO,
          "foundingDate": "2008",
          "contactPoint": { "@type": "ContactPoint", "telephone": "+91-9001991227", "email": "info@grras.tech" }
        })}</script>
      </Helmet>

      <div className="min-h-screen bg-[#0a0a0a] text-white font-sans">
        {/* Navbar */}
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#0a0a0a]/95 backdrop-blur-lg shadow-lg border-b border-white/5' : 'bg-transparent'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <a href="https://lab.grras.tech" className="flex items-center gap-2">
              <img src={GRRAS_LOGO} alt="GRRAS Solutions Logo" className="h-8 sm:h-10" />
            </a>
            <div className="hidden md:flex items-center gap-8 text-sm text-gray-300">
              <a href="https://lab.grras.tech" className="hover:text-white transition-colors">Home</a>
              <a href="#exam-sets" className="hover:text-white transition-colors">Courses</a>
              <a href="https://lab.grras.tech/pricing" className="hover:text-white transition-colors">Pricing</a>
              <a href="https://lab.grras.tech/login" className="hover:text-white transition-colors">Login</a>
            </div>
            <a href="https://lab.grras.tech/register" className="bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-all hover:scale-105">
              Start Free
            </a>
          </div>
        </nav>

        {/* Hero */}
        <section className="pt-28 sm:pt-36 pb-16 sm:pb-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-red-900/10 via-transparent to-transparent"></div>
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-red-600/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-orange-600/5 rounded-full blur-3xl"></div>

          <div className="max-w-5xl mx-auto text-center relative z-10">
            <div className="inline-flex items-center gap-2 bg-red-600/10 border border-red-500/20 rounded-full px-4 py-1.5 text-xs text-red-400 mb-6">
              <Award className="w-3.5 h-3.5" /> Red Hat Authorized Partner since 2008
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Clear RHCSA in First Attempt with{' '}
              <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">Real VM Practice</span>
            </h1>
            <p className="text-base sm:text-lg text-gray-400 max-w-3xl mx-auto mb-10 leading-relaxed">
              Practice on a real RHEL 10 Virtual Machine — just like the actual exam. 3 complete exam sets, auto-verified tasks, and hints when you're stuck.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <a href="https://lab.grras.tech/register" className="bg-red-600 hover:bg-red-700 text-white px-8 py-3.5 rounded-lg font-semibold text-base transition-all hover:scale-105 shadow-lg shadow-red-600/20 flex items-center gap-2">
                Start Free Practice <ChevronRight className="w-4 h-4" />
              </a>
              <a href="https://lab.grras.tech/pricing" className="border border-white/20 hover:border-white/40 text-white px-8 py-3.5 rounded-lg font-semibold text-base transition-all hover:bg-white/5">
                View Pricing
              </a>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-8 max-w-3xl mx-auto">
              {[
                { value: '5000+', label: 'Students Certified' },
                { value: '3', label: 'Full Exam Sets' },
                { value: '45', label: 'Questions Per Set' },
                { value: '95%', label: 'Pass Rate' },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <p className="text-2xl sm:text-3xl font-bold text-white">{stat.value}</p>
                  <p className="text-xs sm:text-sm text-gray-500 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Exam Sets */}
        <section id="exam-sets" className="py-16 sm:py-24 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">3 Complete RHCSA Exam Sets — Real Exam Experience</h2>
              <p className="text-gray-400 text-base">Dual VM setup, timed environment, exactly like the actual Red Hat exam.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { set: 1, difficulty: 'Intermediate', color: 'from-blue-500 to-cyan-500', border: 'border-blue-500/20' },
                { set: 2, difficulty: 'Advanced', color: 'from-orange-500 to-red-500', border: 'border-orange-500/20' },
                { set: 3, difficulty: 'Expert', color: 'from-purple-500 to-violet-600', border: 'border-purple-500/20' },
              ].map((exam, i) => (
                <div key={i} className={`bg-white/[0.03] border ${exam.border} rounded-2xl p-6 hover:bg-white/[0.06] transition-all hover:-translate-y-1`}>
                  <div className={`inline-block bg-gradient-to-r ${exam.color} bg-clip-text text-transparent text-sm font-bold mb-2`}>
                    EXAM SET {exam.set}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">RHCSA Exam Set {exam.set}</h3>
                  <ul className="space-y-2.5 text-sm text-gray-300">
                    <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />45 Questions</li>
                    <li className="flex items-center gap-2"><Monitor className="w-4 h-4 text-blue-400 flex-shrink-0" />Dual VM Setup</li>
                    <li className="flex items-center gap-2"><Clock className="w-4 h-4 text-yellow-400 flex-shrink-0" />Timed Environment</li>
                    <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />Auto-Verified Tasks</li>
                  </ul>
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <span className="text-xs text-gray-500">Difficulty: </span>
                    <span className={`text-xs font-semibold bg-gradient-to-r ${exam.color} bg-clip-text text-transparent`}>{exam.difficulty}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <a href="https://lab.grras.tech/register" className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold transition-all hover:scale-105">
                Start Free <ChevronRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </section>

        {/* Topics Covered */}
        <section className="py-16 sm:py-24 px-4 bg-white/[0.02]">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">RHCSA Topics Covered</h2>
              <p className="text-gray-400 text-base">All exam objectives covered with hands-on labs</p>
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              {topics.map((topic, i) => (
                <span key={i} className="bg-white/[0.05] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-gray-300 hover:border-red-500/30 hover:bg-red-500/5 transition-all cursor-default">
                  {topic}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 sm:py-24 px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">How RHCSA Practice Works</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { step: '1', title: 'Register Free', desc: 'Create account in 60 seconds, no credit card needed', icon: Users },
                { step: '2', title: 'Get Your VM', desc: 'A real isolated RHEL 10 VM is assigned to you instantly', icon: Server },
                { step: '3', title: 'Attempt Tasks', desc: 'Complete exam-style tasks just like the real RHCSA', icon: Terminal },
                { step: '4', title: 'Get Results', desc: "Auto-verified pass/fail with hints if you're stuck", icon: CheckCircle },
              ].map((item, i) => (
                <div key={i} className="text-center relative">
                  <div className="w-14 h-14 rounded-full bg-red-600/10 border-2 border-red-600/30 flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-6 h-6 text-red-500" />
                  </div>
                  <div className="text-xs text-red-500 font-bold mb-1">STEP {item.step}</div>
                  <h3 className="text-base font-semibold mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why GRRAS Lab */}
        <section className="py-16 sm:py-24 px-4 bg-white/[0.02]">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4">Why GRRAS Lab for RHCSA Preparation?</h2>
            <p className="text-gray-400 text-center mb-12 text-base">India's most trusted RHCSA practice platform</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {features.map((f, i) => (
                <div key={i} className="bg-white/[0.03] border border-white/10 rounded-xl p-5 hover:border-red-500/30 transition-all">
                  <f.icon className="w-8 h-8 text-red-500 mb-3" />
                  <h3 className="font-semibold text-white mb-1">{f.title}</h3>
                  <p className="text-sm text-gray-400">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="py-16 sm:py-24 px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">Simple, Affordable Pricing</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Free */}
              <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 flex flex-col">
                <h3 className="text-lg font-semibold mb-1">Free Start</h3>
                <p className="text-3xl font-bold mb-4">{fp(0, 0)}</p>
                <ul className="space-y-2 text-sm text-gray-400 mb-6 flex-1">
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />Select free labs</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />Mock test preview</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />No credit card needed</li>
                </ul>
                <a href="https://lab.grras.tech/register" className="block text-center border border-white/20 hover:border-white/40 text-white py-2.5 rounded-lg font-medium transition-all hover:bg-white/5">Start Free</a>
              </div>
              {/* RHCSA Course */}
              <div className="bg-gradient-to-b from-red-900/20 to-red-950/10 border-2 border-red-500/40 rounded-2xl p-6 flex flex-col relative">
                <span className="absolute -top-3 right-4 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full">MOST POPULAR</span>
                <h3 className="text-lg font-semibold mb-1">RHCSA Course</h3>
                <p className="text-3xl font-bold mb-4">{fp(499, 19)}</p>
                <ul className="space-y-2 text-sm text-gray-300 mb-6 flex-1">
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />3 Full RHCSA Exam Sets</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />42 Labs + Real RHEL 10 VM</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />Lifetime Access</li>
                </ul>
                <a href="https://lab.grras.tech/register" className="block text-center bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-lg font-semibold transition-all">Start RHCSA Prep</a>
              </div>
              {/* All Access */}
              <div className="bg-white/[0.03] border border-orange-500/20 rounded-2xl p-6 flex flex-col relative">
                <span className="absolute -top-3 right-4 bg-orange-600 text-white text-xs font-bold px-3 py-1 rounded-full">BEST VALUE</span>
                <h3 className="text-lg font-semibold mb-1">All Access Lifetime</h3>
                <p className="text-3xl font-bold mb-4">{fp(4999, 199)}<span className="text-sm text-gray-400 font-normal"> one-time</span></p>
                <ul className="space-y-2 text-sm text-gray-400 mb-6 flex-1">
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />16 Courses, 249+ Labs</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />All future courses FREE</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />Mock Tests + Projects</li>
                </ul>
                <a href="https://lab.grras.tech/pricing" className="block text-center border border-orange-500/30 hover:border-orange-500/50 text-white py-2.5 rounded-lg font-medium transition-all hover:bg-orange-500/5">Get All Access</a>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-16 sm:py-24 px-4 bg-white/[0.02]">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">Students Who Cleared RHCSA with GRRAS Lab</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {testimonials.map((t, i) => (
                <div key={i} className="bg-white/[0.03] border border-white/10 rounded-xl p-5">
                  <div className="mb-3 rounded-lg overflow-hidden border border-white/10 bg-white">
                    <img src={t.certImage} alt={`${t.name} Certificate`} className="w-full h-auto" />
                  </div>
                  <div className="flex gap-0.5 mb-3">
                    {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 text-yellow-500 fill-yellow-500" />)}
                  </div>
                  <p className="text-sm text-gray-300 mb-4 leading-relaxed">"{t.quote}"</p>
                  <div>
                    <p className="font-semibold text-white text-sm">{t.name}</p>
                    <p className="text-xs text-red-400">{t.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 sm:py-24 px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <div key={i} className="border border-white/10 rounded-xl overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-white/[0.03] transition-colors">
                    <span className="font-medium text-sm sm:text-base pr-4">{faq.q}</span>
                    <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-4 text-sm text-gray-400 leading-relaxed">{faq.a}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Footer CTA */}
        <section className="py-16 sm:py-24 px-4 bg-white/[0.02]">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Ready to Clear RHCSA in First Attempt?</h2>
            <p className="text-gray-400 mb-8">Join 5000+ certified students. Practice on real RHEL 10 VM. Free to start.</p>
            <a href="https://lab.grras.tech/register" className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-3.5 rounded-lg font-semibold text-base transition-all hover:scale-105 shadow-lg shadow-red-600/20">
              Start Free Practice Now <ChevronRight className="w-4 h-4" />
            </a>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-white/10 py-10 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-3">
                <img src={GRRAS_LOGO} alt="GRRAS Solutions" className="h-8" />
                <span className="text-xs text-gray-500">Authorized Red Hat Partner since 2008</span>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-400">
                <a href="https://lab.grras.tech" className="hover:text-white transition-colors">Home</a>
                <a href="https://lab.grras.tech/pricing" className="hover:text-white transition-colors">Pricing</a>
                <a href="https://lab.grras.tech/login" className="hover:text-white transition-colors">Login</a>
                <a href="https://lab.grras.tech/register" className="hover:text-white transition-colors">Register</a>
                <a href="https://lab.grras.tech/devops" className="hover:text-white transition-colors">DevOps Labs</a>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-500">
              <p>&copy; 2026 GRRAS Solutions Pvt. Ltd. All rights reserved.</p>
              <div className="flex items-center gap-4">
                <span>info@grras.tech</span>
                <span>+91-9001991227</span>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default RHCSALanding;
